import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import { Clock, Plus, Trash2, Loader2, CalendarX, AlertCircle } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { key: "sun", label: "Sunday", short: "Sun" },
  { key: "mon", label: "Monday", short: "Mon" },
  { key: "tue", label: "Tuesday", short: "Tue" },
  { key: "wed", label: "Wednesday", short: "Wed" },
  { key: "thu", label: "Thursday", short: "Thu" },
  { key: "fri", label: "Friday", short: "Fri" },
  { key: "sat", label: "Saturday", short: "Sat" },
];

const AdminAvailability = () => {
  const { token } = useAuth();
  const [unavailability, setUnavailability] = useState([]);
  const [unavailableDays, setUnavailableDays] = useState([]);
  const [businessHours, setBusinessHours] = useState({
    start_time: "09:00",
    end_time: "17:00"
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isSavingDays, setIsSavingDays] = useState(false);
  const [newBlockout, setNewBlockout] = useState({
    date: "",
    reason: ""
  });

  const fetchUnavailability = async () => {
    try {
      const response = await axios.get(`${API}/admin/unavailability`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Separate specific dates from recurring days
      const dates = response.data.filter(item => item.type === 'date');
      const days = response.data.filter(item => item.type === 'recurring_day').map(item => item.day);
      const hours = response.data.find(item => item.type === 'business_hours');
      
      setUnavailability(dates);
      setUnavailableDays(days);
      if (hours) {
        setBusinessHours({
          start_time: hours.start_time || "09:00",
          end_time: hours.end_time || "17:00"
        });
      }
    } catch (error) {
      // If endpoint doesn't exist yet, start with empty state
      console.log("Unavailability endpoint not found, using defaults");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUnavailability();
  }, [token]);

  const handleAddBlockout = async (e) => {
    e.preventDefault();
    setIsAdding(true);

    try {
      await axios.post(`${API}/admin/unavailability`, {
        type: "date",
        date: newBlockout.date,
        reason: newBlockout.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Blockout date added");
      setNewBlockout({ date: "", reason: "" });
      fetchUnavailability();
    } catch (error) {
      toast.error("Failed to add blockout date");
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteBlockout = async (blockoutId) => {
    if (!window.confirm("Are you sure you want to remove this blockout?")) return;

    try {
      await axios.delete(`${API}/admin/unavailability/${blockoutId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Blockout removed");
      fetchUnavailability();
    } catch (error) {
      toast.error("Failed to remove blockout");
    }
  };

  const handleDayToggle = (dayKey) => {
    setUnavailableDays(prev => {
      if (prev.includes(dayKey)) {
        return prev.filter(d => d !== dayKey);
      } else {
        return [...prev, dayKey];
      }
    });
  };

  const handleSaveRecurringDays = async () => {
    setIsSavingDays(true);

    try {
      await axios.post(`${API}/admin/unavailability/recurring-days`, {
        days: unavailableDays,
        business_hours: businessHours
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Recurring schedule saved");
    } catch (error) {
      toast.error("Failed to save schedule");
    } finally {
      setIsSavingDays(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-availability">
      {/* Info Banner */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <p className="text-blue-300 font-medium">Default: Available 7 days a week</p>
              <p className="text-blue-400/80 text-sm">
                Use this page to mark days or dates when you are NOT available. 
                All other times within business hours are considered available for scheduling.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="recurring" className="w-full">
        <TabsList className="bg-navy-900 border border-slate-700">
          <TabsTrigger value="recurring" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Weekly Schedule
          </TabsTrigger>
          <TabsTrigger value="specific" className="data-[state=active]:bg-primary data-[state=active]:text-white">
            Specific Dates
          </TabsTrigger>
        </TabsList>

        {/* Recurring Weekly Schedule */}
        <TabsContent value="recurring">
          <Card className="bg-navy-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Clock className="w-5 h-5 text-primary" />
                Weekly Unavailability
              </CardTitle>
              <CardDescription className="text-slate-400">
                Select days of the week when you are NOT available for appointments
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Business Hours */}
              <div className="p-4 rounded-lg bg-navy-950/50 border border-slate-700">
                <p className="text-sm text-slate-400 mb-3">Default Business Hours (when available) - Mountain Time (Denver)</p>
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="space-y-2">
                    <Label htmlFor="start_time" className="text-slate-300">Start Time</Label>
                    <Input
                      id="start_time"
                      type="time"
                      value={businessHours.start_time}
                      onChange={(e) => setBusinessHours({ ...businessHours, start_time: e.target.value })}
                      className="bg-navy-900 border-slate-700 text-white w-32"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_time" className="text-slate-300">End Time</Label>
                    <Input
                      id="end_time"
                      type="time"
                      value={businessHours.end_time}
                      onChange={(e) => setBusinessHours({ ...businessHours, end_time: e.target.value })}
                      className="bg-navy-900 border-slate-700 text-white w-32"
                    />
                  </div>
                </div>
              </div>

              {/* Day Selection */}
              <div className="space-y-3">
                <p className="text-sm text-slate-400">Mark days as UNAVAILABLE:</p>
                <div className="grid grid-cols-7 gap-2">
                  {DAYS_OF_WEEK.map((day) => {
                    const isUnavailable = unavailableDays.includes(day.key);
                    return (
                      <button
                        key={day.key}
                        onClick={() => handleDayToggle(day.key)}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          isUnavailable
                            ? "bg-red-500/20 border-red-500/50 text-red-400"
                            : "bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20"
                        }`}
                        data-testid={`day-toggle-${day.key}`}
                      >
                        <span className="block font-medium">{day.short}</span>
                        <span className="block text-xs mt-1">
                          {isUnavailable ? "OFF" : "ON"}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="bg-navy-950/50 p-3 rounded-lg border border-slate-700">
                  <p className="text-xs text-slate-400">
                    <span className="text-green-400 font-medium">Green (ON)</span> = Available for scheduling
                  </p>
                  <p className="text-xs text-slate-400">
                    <span className="text-red-400 font-medium">Red (OFF)</span> = Not available • <span className="text-yellow-400">Days remain OFF until you manually turn them back ON</span>
                  </p>
                </div>
              </div>

              <Button
                onClick={handleSaveRecurringDays}
                disabled={isSavingDays}
                className="bg-primary hover:bg-primary/90 text-white"
                data-testid="save-recurring-btn"
              >
                {isSavingDays ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Weekly Schedule"
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Specific Date Blockouts */}
        <TabsContent value="specific">
          <div className="space-y-6">
            {/* Add Blockout Form */}
            <Card className="bg-navy-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Plus className="w-5 h-5 text-primary" />
                  Add Blockout Date
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Block specific dates when you are not available (holidays, vacations, etc.)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddBlockout} className="flex flex-wrap gap-4 items-end">
                  <div className="space-y-2">
                    <Label htmlFor="date" className="text-slate-300">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={newBlockout.date}
                      onChange={(e) => setNewBlockout({ ...newBlockout, date: e.target.value })}
                      required
                      className="bg-navy-900 border-slate-700 text-white w-40"
                      data-testid="blockout-date"
                    />
                  </div>
                  <div className="space-y-2 flex-1 min-w-[200px]">
                    <Label htmlFor="reason" className="text-slate-300">Reason (optional)</Label>
                    <Input
                      id="reason"
                      type="text"
                      value={newBlockout.reason}
                      onChange={(e) => setNewBlockout({ ...newBlockout, reason: e.target.value })}
                      placeholder="e.g., Holiday, Vacation, Conference"
                      className="bg-navy-900 border-slate-700 text-white"
                      data-testid="blockout-reason"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isAdding}
                    className="bg-primary hover:bg-primary/90 text-white"
                    data-testid="blockout-submit"
                  >
                    {isAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Blockout"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Blockout List */}
            <Card className="bg-navy-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <CalendarX className="w-5 h-5 text-primary" />
                  Blocked Dates ({unavailability.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {unavailability.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700 hover:bg-transparent">
                          <TableHead className="text-slate-400">Date</TableHead>
                          <TableHead className="text-slate-400">Reason</TableHead>
                          <TableHead className="text-slate-400">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unavailability.map((blockout) => (
                          <TableRow
                            key={blockout.id}
                            className="border-slate-800 hover:bg-navy-800/50"
                            data-testid={`blockout-row-${blockout.id}`}
                          >
                            <TableCell className="text-white font-medium">
                              {blockout.date}
                            </TableCell>
                            <TableCell className="text-slate-300">
                              {blockout.reason || "-"}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteBlockout(blockout.id)}
                                className="text-slate-400 hover:text-red-400"
                                data-testid={`delete-blockout-${blockout.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">
                    No specific dates blocked. You're available on all dates within your weekly schedule.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAvailability;
